<script lang="ts">
    import type {User} from 'typings/portaro.be.types';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import {onDestroy, onMount} from 'svelte';
    import {KpUserFinancePanelService} from './kp-user-finance-panel.service';
    import {exists, isFunction} from 'shared/utils/custom-utils';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import TransactionMoneyIndicator from './TransactionMoneyIndicator.svelte';
    import TransactionGroupItem from './TransactionGroupItem.svelte';
    import {CASH_PAYMENT_PROVIDER_TYPE, MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME} from './types';
    import type {
        GroupTransaction,
        Transaction,
        TransactionSums
    } from './types';

    export let user: User;
    export let enabledPaymentProviderNames: string[];
    export let userCanDeletePayment = true;
    export let userCanDeleteDebt = true;

    const localize = getLocalization();
    const service = getInjector().getByToken<KpUserFinancePanelService>(KpUserFinancePanelService.serviceName);

    let loading = true;
    let transactions: Transaction[];
    let groups: GroupTransaction[];
    let sums: TransactionSums;
    let deregisterUpdate: () => void;

    onMount(async () => {
        deregisterUpdate = service.addUpdateFinancePanelHandler(handleUpdateFinancePanel);
        await loadAllData();
    });

    onDestroy(() => {
        if (isFunction(deregisterUpdate)) {
            deregisterUpdate();
        }
    });

    const handleUpdateFinancePanel = () => {
        loadAllData();
    }

    const handlePayAllUserDebts = async (paymentProvider: string) => {
        const successful = await service.payAllUserDebts(paymentProvider, user);
        if (!successful) {
            return;
        }

        await loadAllData();
    }

    const handleForgiveTransaction = () => {
        sums = service.calculateSums(transactions, groups);
    }

    async function loadAllData() {
        loading = true;
        transactions = await service.loadAllTransactions(user.id);
        groups = service.calculateGroups(transactions);
        sums = service.calculateSums(transactions, groups);
        loading = false;
    }
</script>

<KpGenericPanel additionalClasses="kp-user-finance-panel" hasBodyPadding="{false}">
    <span slot="heading">{localize(/* @kp-localization konto.StavKonta */ 'konto.StavKonta')}</span>

    {#if loading || (!exists(transactions) || !exists(groups) || !exists(sums))}
        <div class="loading-container">
            <KpLoadingBlock size="sm"/>
        </div>
    {:else}
        <div class="finance-panel-content-container">
            <ul class="group-transactions-list">
                {#each groups as group}
                    <li class="group-transaction-item">
                        <TransactionGroupItem {group}
                                              {userCanDeleteDebt}
                                              {userCanDeletePayment}
                                              transactions="{service.filterAllByGroup(transactions, group)}"
                                              on:forgive-transaction={handleForgiveTransaction}/>
                    </li>
                {/each}
            </ul>

            <div class="sums-container">
                <div class="sum-item">
                    <span>{localize(/* @kp-localization konto.CreditsSum */ 'konto.CreditsSum')}</span>
                    <TransactionMoneyIndicator amount="{sums.creditsSum}"/>
                </div>

                <div class="sum-item">
                    <span>{localize(/* @kp-localization konto.DebtsSum */ 'konto.DebtsSum')}</span>
                    <TransactionMoneyIndicator amount="{sums.debtsSum}"/>
                </div>

                <div class="sum-item">
                    <span>{localize(/* @kp-localization commons.soucet */ 'commons.soucet')}</span>

                    <span class="money-sum">
                        {#if sums.creditsSum > 0 && Math.abs(sums.debtsSum) > 0}
                            <span class="positive">{sums.creditsSum}</span>
                            <span class="negative">- {Math.abs(sums.debtsSum)}</span>
                            =
                        {/if}

                        <span class:positive={sums.sum > 0} class:negative={sums.sum < 0}>{sums.sum}Kč</span>
                    </span>
                </div>
            </div>

            {#if enabledPaymentProviderNames.length > 0}
                <div class="action-buttons-container">
                    {#each enabledPaymentProviderNames as paymentProvider, index}
                        {#if service.canShowPayButtonOf(paymentProvider, sums.debtsSum)}
                            <KpButton isBlock
                                      buttonSize="sm"
                                      buttonStyle="{index === 0 ? 'accent-blue-new' : 'default'}"
                                      on:click={() => handlePayAllUserDebts(paymentProvider)}>

                                {#if paymentProvider === CASH_PAYMENT_PROVIDER_TYPE}
                                    {localize(/* @kp-localization payment.payByCash */ 'payment.payByCash')}
                                {:else if paymentProvider === MANUAL_TERMINAL_PAYMENT_PROVIDER_NAME}
                                    {localize(/* @kp-localization payment.PayManuallyViaTerminal */ 'payment.PayManuallyViaTerminal')}
                                {:else}
                                    {localize(/* @kp-localization payment.payOnline */ 'payment.payOnline')}
                                {/if}
                            </KpButton>
                        {/if}
                    {/each}
                </div>
            {/if}
        </div>
    {/if}
</KpGenericPanel>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    @row-height: 40px;

    .finance-panel-content-container,
    .group-transactions-list,
    .sums-container,
    .action-buttons-container {
        display: flex;
        flex-direction: column;
    }

    .sum-item {
        font-weight: bold;
        display: flex;
        align-items: center;
        gap: @padding-base-horizontal;
        padding: 0 @panel-body-padding;
        height: @row-height;

        &:not(:last-child) {
            border-bottom: 1px solid @panel-default-border;
        }

        .money-sum {
            margin-left: auto;

            .positive {
                color: var(--success-green);
            }

            .negative {
                color: var(--danger-red);
            }
        }
    }

    .loading-container {
        display: flex;
        justify-content: center;
        padding: 25px @panel-body-padding;
    }

    .action-buttons-container {
        gap: @padding-base-vertical;
        padding: @panel-body-padding;
        border-top: 1px solid @panel-default-border;

        &:empty {
            display: none;
        }
    }
</style>
